import React, { useContext, useState, useRef } from "react";
import styled from "styled-components";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { THEME_COLORS } from "../../ThemeConfig";
import logo from "../../assets/images/header/logoimg.svg";
import notification from "../../assets/images/notification-green.svg";
import NotificationModal from "../includes/modals/NotificationModal";
import LogoutModal from "../includes/modals/LogoutModal";
import Login from "../screens/loginscreens/Login";
import { Context } from "../../contexts/Store";

const EXPERT_DASHBOARD_URL = "https://expert.mymindmitra.com/";

export default function DashboardHeader({
  unreadNotifictaionsCount = 0,
  unreadNotifications = [],
  getHeaderNotifications,
  loginModalOpen = false,
  setLoginModalOpen,
  loginModalInitialPage = "login",
}) {
  const { state } = useContext(Context);
  const auth = state.user_details?.is_verified;
  const navigate = useNavigate();
  const location = useLocation();
  const id = state.session_details?.id || "";

  const [loginOpen, setLoginOpen] = useState(false);
  const loginCloseTimeoutRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const isDashboardActive =
    location.pathname === "/dashboard" ||
    location.pathname === "/describe" ||
    location.pathname === "/schedule" ||
    location.pathname === "/fulfill" ||
    location.pathname === "/create-session" ||
    location.pathname === "/session-confirmed" ||
    location.pathname === "/notifications" ||
    location.pathname === `/session/${id}` ||
    location.pathname === `/view-summary-report/${id}` ||
    location.pathname === `/cancel-session/${id}` ||
    location.pathname?.startsWith("/payment/");

  const isAccountActive =
    location.pathname === "/account" ||
    location.pathname === "/privacy-policy" ||
    location.pathname === "/terms-and-conditions" ||
    location.pathname === "/refund-policy" ||
    location.pathname === "/faq" ||
    location.pathname === "/transaction-history";

  const handleBookSession = () => {
    navigate("/create-session");
  };

  const handleLoginDropdownEnter = () => {
    if (loginCloseTimeoutRef.current) {
      clearTimeout(loginCloseTimeoutRef.current);
      loginCloseTimeoutRef.current = null;
    }
    setLoginOpen(true);
  };

  const handleLoginDropdownLeave = () => {
    loginCloseTimeoutRef.current = setTimeout(() => setLoginOpen(false), 200);
  };

  return (
    <HeaderWrap>
      <HeaderContainer>
        <LeftDiv>
          <Logo to="/">
            <LogoImg src={logo} alt="MindMitra" />
            <LogoTextWrap>
              <LogoLine1>
                <LogoPartGreen>Mind</LogoPartGreen>
                <LogoPartBlue>Mitra</LogoPartBlue>
              </LogoLine1>
              <LogoLine2>Wellness Redefined</LogoLine2>
            </LogoTextWrap>
          </Logo>
        </LeftDiv>

        <NavCenter>
          <StyledNavLink to="/" end>Home</StyledNavLink>
          <StyledNavLink to="/about-us">About us</StyledNavLink>
          <StyledNavLink to="/concerns" className={location.pathname === "/concerns" || location.pathname?.startsWith("/concerns/") ? "active" : ""}>Concerns</StyledNavLink>

          {auth ? (
            <>
              <DashboardNavLink to="/dashboard" className={isDashboardActive ? "active" : ""}>Dashboard</DashboardNavLink>
              <StyledNavLink to="/account" className={isAccountActive ? "active" : ""}>My Account</StyledNavLink>
            </>
          ) : (
            <DropdownWrapper
              onMouseEnter={handleLoginDropdownEnter}
              onMouseLeave={handleLoginDropdownLeave}
            >
              <DropdownTrigger as="span">Login</DropdownTrigger>
              {loginOpen && (
                <DropdownMenu onMouseEnter={handleLoginDropdownEnter} onMouseLeave={handleLoginDropdownLeave}>
                  <DropdownItemButton
                    type="button"
                    onClick={() => {
                      setLoginOpen(false);
                      setLoginModalOpen(true);
                    }}
                  >
                    <DropdownItemTitle>Login as Client</DropdownItemTitle>
                    <DropdownItemDesc>Book sessions and get therapy support</DropdownItemDesc>
                  </DropdownItemButton>
                  <DropdownItem
                    as="a"
                    href={EXPERT_DASHBOARD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setLoginOpen(false)}
                  >
                    <DropdownItemTitle>Login as Expert</DropdownItemTitle>
                    <DropdownItemDesc>Provide therapy and manage your practice</DropdownItemDesc>
                  </DropdownItem>
                </DropdownMenu>
              )}
            </DropdownWrapper>
          )}
        </NavCenter>

        <NavDesktop>
          {auth && getHeaderNotifications && (
            <NotificationBtn onClick={() => setIsModalOpen(true)} type="button">
              <NotificationImage src={notification} alt="Notifications" />
              {unreadNotifictaionsCount > 0 && <NotificationRedSignal />}
            </NotificationBtn>
          )}
          {auth && (
            <LogoutBtn type="button" onClick={() => setLogoutModalOpen(true)}>Logout</LogoutBtn>
          )}
          <BookButton type="button" onClick={handleBookSession}>Book session</BookButton>
        </NavDesktop>

        <MobileMenuBtn type="button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
          <MenuIcon className={mobileMenuOpen ? "open" : ""}>
            <span />
            <span />
            <span />
          </MenuIcon>
        </MobileMenuBtn>
      </HeaderContainer>

      {mobileMenuOpen && (
        <MobileMenu>
          <MobileLink to="/" onClick={() => setMobileMenuOpen(false)} className={location.pathname === "/" ? "active" : ""}>Home</MobileLink>
          <MobileLink to="/about-us" onClick={() => setMobileMenuOpen(false)} className={location.pathname === "/about-us" ? "active" : ""}>About us</MobileLink>
          <MobileLink to="/concerns" onClick={() => setMobileMenuOpen(false)} className={location.pathname === "/concerns" || location.pathname?.startsWith("/concerns/") ? "active" : ""}>Concerns</MobileLink>
          {auth ? (
            <>
              <MobileLink to="/dashboard" onClick={() => setMobileMenuOpen(false)} className={isDashboardActive ? "active" : ""}>Dashboard</MobileLink>
              <MobileLink to="/account" onClick={() => setMobileMenuOpen(false)} className={isAccountActive ? "active" : ""}>My Account</MobileLink>
              {getHeaderNotifications && (
                <MobileBtn type="button" onClick={() => { setIsModalOpen(true); setMobileMenuOpen(false); }}>Notifications</MobileBtn>
              )}
              <MobileBtn type="button" onClick={() => { setLogoutModalOpen(true); setMobileMenuOpen(false); }}>Logout</MobileBtn>
            </>
          ) : (
            <>
              <MobileBtn type="button" onClick={() => { setLoginModalOpen(true); setMobileMenuOpen(false); }}>Login (Client)</MobileBtn>
              <MobileLink as="a" href="https://expert.mymindmitra.com/" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>Login (Expert)</MobileLink>
            </>
          )}
          <MobileBookBtn type="button" onClick={() => { handleBookSession(); setMobileMenuOpen(false); }}>Book session</MobileBookBtn>
        </MobileMenu>
      )}

      {isModalOpen && getHeaderNotifications && (
        <NotificationModal
          notifications={unreadNotifications}
          closeModal={() => setIsModalOpen(false)}
        />
      )}

      <LogoutModal
        modalOpen={logoutModalOpen ? "Logout" : ""}
        onClose={() => setLogoutModalOpen(false)}
      />

      {loginModalOpen && setLoginModalOpen && (
        <AuthModalOverlay onClick={() => setLoginModalOpen(false)}>
          <AuthModalContent onClick={(e) => e.stopPropagation()}>
            <Login
              isModal
              initialPage={loginModalInitialPage || "login"}
              onClose={() => setLoginModalOpen(false)}
              onSuccess={() => setLoginModalOpen(false)}
            />
          </AuthModalContent>
        </AuthModalOverlay>
      )}
    </HeaderWrap>
  );
}

const HeaderWrap = styled.div`
  position: relative;
  z-index: 1000;
`;

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1000;
  border-bottom: 1px solid ${THEME_COLORS.light_300};
  background: ${THEME_COLORS.card_bg};
  box-shadow: ${THEME_COLORS.elevation_user_u4};
  height: 70px;
  padding: 8px 32px;
  @media all and (max-width: 768px) {
    padding: 8px 20px;
  }
`;
const LeftDiv = styled.div`
  display: flex;
  align-items: center;
`;
const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: inherit;
`;
const LogoImg = styled.img`
  width: 35px;
  height: auto;
  @media all and (max-width: 480px) {
    width: 32px;
  }
`;
const LogoTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
`;
const LogoLine1 = styled.span`
  display: inline-flex;
  font-family: "DM_sans_medium";
  font-size: 18px;
  @media all and (max-width: 480px) {
    font-size: 16px;
  }
`;
const LogoPartGreen = styled.span`
  color: ${THEME_COLORS.primary};
`;
const LogoPartBlue = styled.span`
  color: ${THEME_COLORS.secondary};
`;
const LogoLine2 = styled.span`
  font-family: "DM_sans";
  font-size: 12px;
  color: ${THEME_COLORS.text_para};
  @media all and (max-width: 480px) {
    font-size: 11px;
  }
`;
const NavCenter = styled.nav`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
  @media all and (max-width: 1024px) {
    display: none;
  }
`;
const NavDesktop = styled.nav`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  flex-shrink: 0;
  @media all and (max-width: 1024px) {
    display: none;
  }
`;
const StyledNavLink = styled(NavLink)`
  font-family: "DM_sans";
  font-size: 15px;
  color: ${THEME_COLORS.text_title};
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    color: ${THEME_COLORS.primary};
  }
  &.active {
    color: ${THEME_COLORS.primary};
    font-family: "DM_sans_medium";
  }
`;
const DashboardNavLink = styled(Link)`
  font-family: "DM_sans";
  font-size: 15px;
  color: ${THEME_COLORS.text_title};
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    color: ${THEME_COLORS.primary};
  }
  &.active {
    color: ${THEME_COLORS.primary};
    font-family: "DM_sans_medium";
  }
`;
const LogoutBtn = styled.button`
  font-family: "DM_sans";
  font-size: 15px;
  color: ${THEME_COLORS.text_title};
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 0;
  white-space: nowrap;
  &:hover {
    color: ${THEME_COLORS.primary};
  }
`;
const DropdownWrapper = styled.div`
  position: relative;
`;
const DropdownTrigger = styled.span`
  font-family: "DM_sans";
  font-size: 15px;
  color: ${THEME_COLORS.text_title};
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    color: ${THEME_COLORS.primary};
  }
`;
const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0;
  padding-top: 4px;
  min-width: 220px;
  background: ${THEME_COLORS.white};
  border-radius: 8px;
  box-shadow: ${THEME_COLORS.elevation_user_u3};
  padding: 8px 0 8px 0;
  z-index: 1001;
`;
const DropdownItem = styled(Link)`
  display: block;
  padding: 10px 16px;
  font-family: "DM_sans";
  font-size: 14px;
  color: ${THEME_COLORS.text_title};
  text-decoration: none;
  text-align: left;
  border: none;
  width: 100%;
  cursor: pointer;
  background: none;
  &:hover {
    background: ${THEME_COLORS.light_200};
    color: ${THEME_COLORS.primary};
  }
`;
const DropdownItemButton = styled.button`
  display: block;
  padding: 10px 16px;
  font-family: "DM_sans";
  font-size: 14px;
  color: ${THEME_COLORS.text_title};
  text-decoration: none;
  text-align: left;
  border: none;
  width: 100%;
  cursor: pointer;
  background: none;
  &:hover {
    background: ${THEME_COLORS.light_200};
    color: ${THEME_COLORS.primary};
  }
`;
const DropdownItemTitle = styled.span`
  display: block;
  font-family: "DM_sans_medium";
  font-size: 14px;
  margin-bottom: 2px;
`;
const DropdownItemDesc = styled.span`
  display: block;
  font-family: "DM_sans";
  font-size: 12px;
  color: ${THEME_COLORS.text_para};
`;
const BookButton = styled.button`
  padding: 8px 18px;
  background: ${THEME_COLORS.chips_green_on_container};
  color: ${THEME_COLORS.white};
  font-family: "DM_sans_medium";
  font-size: 14px;
  border: none;
  border-radius: 32px;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    opacity: 0.9;
  }
`;
const NotificationBtn = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  position: relative;
`;
const NotificationImage = styled.img`
  width: 22px;
  height: 22px;
`;
const NotificationRedSignal = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 8px;
  background: #f00;
  border-radius: 50%;
`;
const MobileMenuBtn = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  @media all and (max-width: 1024px) {
    display: block;
  }
`;
const MenuIcon = styled.div`
  width: 24px;
  height: 18px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  span {
    display: block;
    height: 2px;
    background: ${THEME_COLORS.text_title};
    transition: 0.3s;
  }
  &.open span:nth-child(1) {
    transform: rotate(45deg) translate(5px, 5px);
  }
  &.open span:nth-child(2) {
    opacity: 0;
  }
  &.open span:nth-child(3) {
    transform: rotate(-45deg) translate(5px, -5px);
  }
`;
const MobileMenu = styled.div`
  display: none;
  @media all and (max-width: 1024px) {
    display: flex;
    flex-direction: column;
    background: ${THEME_COLORS.white};
    border-bottom: 1px solid ${THEME_COLORS.light_300};
    padding: 16px 20px;
    gap: 12px;
    position: absolute;
    top: 70px;
    left: 0;
    right: 0;
    z-index: 99;
    box-shadow: ${THEME_COLORS.elevation_user_u3};
  }
`;
const MobileLink = styled(Link)`
  font-family: "DM_sans";
  font-size: 16px;
  color: ${THEME_COLORS.text_title};
  text-decoration: none;
  padding: 10px 0;
  &:hover {
    color: ${THEME_COLORS.primary};
  }
  &.active {
    color: ${THEME_COLORS.primary};
    font-family: "DM_sans_medium";
  }
`;
const MobileBtn = styled.button`
  font-family: "DM_sans";
  font-size: 16px;
  color: ${THEME_COLORS.text_title};
  background: none;
  border: none;
  padding: 10px 0;
  text-align: left;
  cursor: pointer;
  &:hover {
    color: ${THEME_COLORS.primary};
  }
`;
const MobileBookBtn = styled.button`
  margin-top: 8px;
  padding: 12px 20px;
  background: ${THEME_COLORS.chips_green_on_container};
  color: ${THEME_COLORS.white};
  font-family: "DM_sans_medium";
  font-size: 16px;
  border: none;
  border-radius: 32px;
  cursor: pointer;
`;
const AuthModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
`;
const AuthModalContent = styled.div`
  max-width: 440px;
  width: 100%;
  margin: auto;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  background: transparent;
`;
